/**
 * /admin/settings — the platform's own settings, owner-only server-side.
 *
 * A key-value endpoint behind a fixed form: the five keys the platform actually
 * has are rendered as named fields rather than as an editable key-value grid,
 * because root_mode in particular decides what the site serves at "/" and a
 * free-text key box invites typing `root_made`.
 *
 * The form posts every field it renders and the endpoint reports back which
 * keys actually moved (changedKeys). The success message reads off THAT rather
 * than off the submission, so saving with one field touched does not claim five
 * settings changed.
 */

import { useEffect, useState } from 'react';
import { apiGet, apiPut } from '../lib/api.js';

/**
 * Every key the form knows, in reading order: identity, then contact, then
 * routing. This is also the label map the success message reads off, so a key
 * cannot be renamed in one place and stay stale in the other.
 */
const LABELS = {
  platform_name:          'Platform name',
  platform_logo_url:      'Platform logo URL',
  platform_contact_email: 'Platform contact email',
  root_mode:              'Root mode',
  default_venue_id:       'Default venue ID',
};

const KEYS = Object.keys(LABELS);

/** The plain text inputs. root_mode and default_venue_id render by hand. */
const TEXT_FIELDS = [
  { key: 'platform_name' },
  { key: 'platform_logo_url' },
  { key: 'platform_contact_email', type: 'email' },
];

/** A missing setting reads as null; the inputs are controlled and want ''. */
function toForm(settings) {
  return Object.fromEntries(KEYS.map((key) => [key, settings?.[key] ?? '']));
}

export default function PlatformSettings() {
  const [state, setState] = useState({ status: 'loading' });
  const [form, setForm] = useState(toForm(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let cancelled = false;

    apiGet('/api/admin/platform/settings').then(({ data, error: err }) => {
      if (cancelled) return;

      if (err) { setState({ status: 'error', error: err }); return; }

      setForm(toForm(data?.settings));
      setState({ status: 'ready' });
    });

    return () => { cancelled = true; };
  }, []);

  const set = (key) => (event) =>
    setForm((f) => ({ ...f, [key]: event.target.value }));

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);

    const { data, error: err } = await apiPut('/api/admin/platform/settings', form);

    setBusy(false);

    if (err) { setError(err); return; }

    // Reload from the response rather than keeping local state: the endpoint
    // coerces values, so what was saved is not always what was typed.
    setForm(toForm(data?.settings));

    const changed = data?.changedKeys ?? [];
    setNotice(
      changed.length
        ? `Saved ${changed.map((k) => LABELS[k] ?? k).join(', ')}.`
        : 'No changes to save.'
    );
  };

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Platform settings</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Platform settings</h1>
        <p className="hd-meta">The platform's name, branding and front door.</p>
      </div>

      <form className="hd-admin-form hd-stack" onSubmit={onSubmit}>
        {TEXT_FIELDS.map((field) => (
          <div className="hd-field" key={field.key}>
            <label className="hd-label" htmlFor={field.key}>{LABELS[field.key]}</label>
            <input
              id={field.key}
              className="hd-input"
              type={field.type ?? 'text'}
              value={form[field.key]}
              onChange={set(field.key)}
            />
          </div>
        ))}

        <div className="hd-field">
          <label className="hd-label" htmlFor="root_mode">Root mode</label>
          <select
            id="root_mode"
            className="hd-select"
            value={form.root_mode}
            onChange={set('root_mode')}
          >
            <option value="platform">Platform — the multi-venue landing page</option>
            <option value="venue">Venue — serve one venue's page at /</option>
          </select>
        </div>

        {/* Only rendered under 'venue', where it is required. Under 'platform'
            it is dead config, and a field that does nothing invites someone to
            fill it in and expect an effect. */}
        {form.root_mode === 'venue' && (
          <div className="hd-field">
            <label className="hd-label" htmlFor="default_venue_id">Default venue ID</label>
            <input
              id="default_venue_id"
              className="hd-input"
              inputMode="numeric"
              value={form.default_venue_id}
              onChange={set('default_venue_id')}
            />
            <span className="hd-hint">
              The venue served at / — required while root mode is Venue.
            </span>
          </div>
        )}

        {error  && <div className="hd-badge hd-badge-danger"  role="alert">{error}</div>}
        {notice && <div className="hd-badge hd-badge-success" role="status">{notice}</div>}

        <div className="hd-row">
          <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
